import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { format } from "date-fns";
import ko from "date-fns/locale/ko";
import { FeedData } from "../Data/FeedData"; // FeedData import
import { CommentData } from "../Data/FeedCommentData";

const FeedItem = () => {
  const [likeState, setLikeState] = useState(
    FeedData.map((post) => ({
      id: post.id,
      isLiked: false,
      likeCount: post.likeNums,
    }))
  );
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [currentPost, setCurrentPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preloadedImages, setPreloadedImages] = useState({});
  const username = "dongsu";

  useEffect(() => {
    if (isCommentModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isCommentModalOpen]);

  useEffect(() => {
    const preloadImages = () => {
      let loadedCount = 0;
      const totalImages = FeedData.flatMap((post) =>
        Array.isArray(post.postImg) ? post.postImg : [post.postImg]
      );
      totalImages.forEach((src) => {
        const img = new Image();
        img.src = src;
        img.onload = () => {
          setPreloadedImages((prev) => ({ ...prev, [src]: img.src }));
          loadedCount++;
          if (loadedCount === totalImages.length) {
            setLoading(false);
          }
        };
      });
    };

    preloadImages();
  }, []); // 빈 배열로 인해 컴포넌트 마운트 시에만 실행됨

  const handleLikeClick = useCallback((id) => {
    setLikeState((prevState) =>
      prevState.map((item) =>
        item.id === id
          ? {
              ...item,
              isLiked: !item.isLiked,
              likeCount: item.isLiked ? item.likeCount - 1 : item.likeCount + 1,
            }
          : item
      )
    );
  }, []);

  const handleCommentClick = useCallback((post) => {
    setCurrentPost(post);
    setIsCommentModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsCommentModalOpen(false);
    setCurrentPost(null);
  }, []);

  const handleCommentChange = useCallback((event) => {
    setComment(event.target.value);
  }, []);

  const handleCommentSubmit = useCallback(() => {
    if (comment.trim()) {
      const now = new Date();
      setComments((prevComments) => [
        ...prevComments,
        { text: `${username}: ${comment}`, timestamp: now },
      ]);
      setComment("");
    }
  }, [comment]);

  const formatCommentTime = useCallback((timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);

    if (diff < 60 * 60 * 1000) {
      return `${Math.floor(diff / (60 * 1000))}분 전`;
    } else if (diff < 24 * 60 * 60 * 1000) {
      return `${Math.floor(diff / (60 * 60 * 1000))}시간 전`;
    } else {
      return format(new Date(timestamp), "yyyy-MM-dd", { locale: ko });
    }
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  // 로딩 중일 때 표시할 컴포넌트
  if (loading) {
    return (
      <LoadingScreen>
        <p>Loading...</p>
      </LoadingScreen>
    );
  }

  return (
    <FeedWrap>
      {FeedData.map((post) => {
        const likeInfo = likeState.find((item) => item.id === post.id);
        return (
          <Post key={post.id}>
            <Header>
              <UserAvatar src={post.userSrc} alt={post.userAlt} />
              <UserName>{post.userName}</UserName>
            </Header>
            <PostImageWrapper>
              {Array.isArray(post.postImg) ? (
                <Slider {...sliderSettings}>
                  {post.postImg.map((img, index) => (
                    <PostImage
                      key={index}
                      src={preloadedImages[img] || img}
                      alt={post.postAlt}
                    />
                  ))}
                </Slider>
              ) : (
                <PostImage
                  src={preloadedImages[post.postImg] || post.postImg}
                  alt={post.postAlt}
                />
              )}
            </PostImageWrapper>
            <PostActions>
              <ActionButton onClick={() => handleLikeClick(post.id)}>
                <Icon
                  className={`like ${likeInfo.isLiked ? "liked" : "not-liked"}`}
                >
                  {likeInfo.isLiked ? "❤️" : "🖤"}
                </Icon>
              </ActionButton>
              <ActionButton onClick={() => handleCommentClick(post)}>
                <Icon className="comment">💬</Icon>
              </ActionButton>
            </PostActions>
            <PostInfo>
              <Likes>{likeInfo.likeCount} likes</Likes>
              <Caption>
                <UserName>{post.userName}</UserName> {post.comment}
              </Caption>
            </PostInfo>
          </Post>
        );
      })}

      {isCommentModalOpen && currentPost && (
        <CommentModal>
          <ModalContent>
            <PostImageWrapper>
              {Array.isArray(currentPost.postImg) ? (
                <Slider {...sliderSettings}>
                  {currentPost.postImg.map((img, index) => (
                    <PostImageModal
                      key={index}
                      src={preloadedImages[img] || img}
                      alt={currentPost.postAlt}
                    />
                  ))}
                </Slider>
              ) : (
                <PostImageModal
                  src={
                    preloadedImages[currentPost.postImg] || currentPost.postImg
                  }
                  alt={currentPost.postAlt}
                />
              )}
            </PostImageWrapper>
            <CommentSection>
              <ModalHeader>
                <h2>댓글</h2>
                <CloseButton onClick={handleCloseModal}>×</CloseButton>
              </ModalHeader>
              <CommentInput
                type="text"
                value={comment}
                onChange={handleCommentChange}
                placeholder="친구에게 하고 싶은 말을 적어주세요"
              />
              <SubmitButton onClick={handleCommentSubmit}>
                작성하기
              </SubmitButton>
              <CommentList>
                {comments.map((commentObj, index) => (
                  <CommentItem key={index}>
                    <div>{commentObj.text}</div>
                    <TimeStamp>
                      {formatCommentTime(commentObj.timestamp)}
                    </TimeStamp>
                  </CommentItem>
                ))}
              </CommentList>
            </CommentSection>
          </ModalContent>
        </CommentModal>
      )}
    </FeedWrap>
  );
};

export default FeedItem;

// Styled Components

const LoadingScreen = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #f0f0f0;
  p {
    font-size: 24px;
    color: #333;
  }
`;

const FeedWrap = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 10px;
  margin-top: 20px;

  @media (max-width: 767px) {
    max-width: 90%;
    padding: 0 10px;
    margin-bottom: 40px;
    overflow-x: hidden;
  }
`;

const Post = styled.div`
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
  @media (max-width: 767px) {
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
`;

const UserAvatar = styled.img`
  border-radius: 50%;
  width: 40px;
  height: 40px;
  margin-right: 10px;
`;

const UserName = styled.span`
  font-weight: bold;
`;

const PostImageWrapper = styled.div`
  .slick-slider {
    .slick-list {
      .slick-track {
        .slick-slide {
          display: flex;
          justify-content: center;
        }
      }
    }
  }
`;

const PostImage = styled.img`
  width: 100%;
  height: auto;
`;

const PostActions = styled.div`
  display: flex;
  padding: 10px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 24px;
  margin-right: 10px;
`;

const Icon = styled.span`
  &.like {
    font-size: 24px;
    transition: color 0.3s ease;

    &.liked {
      color: red;
    }

    &.not-liked {
      color: black;
    }
  }
`;

const PostInfo = styled.div`
  padding: 10px;
`;

const Likes = styled.div`
  font-weight: bold;
  margin-bottom: 5px;
`;

const Caption = styled.div`
  color: #333;
`;

// Modal Components

const CommentModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  max-width: 800px;
  width: 90%;
  height: 80%;
`;

const PostImageModal = styled.img`
  width: 100%;
  height: auto;
`;

const CommentSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 10px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #ddd;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
`;

const CommentInput = styled.input`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 10px;
`;

const SubmitButton = styled.button`
  background: #0095f6;
  color: white;
  padding: 10px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 10px;
`;

const CommentList = styled.ul`
  list-style: none;
  padding: 0;
  flex-grow: 1;
  overflow-y: auto;
`;

const CommentItem = styled.li`
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
`;

const TimeStamp = styled.span`
  color: #999;
  font-size: 12px;
`;
